import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SearchAgentResult } from '../system/search';
import {
  PermissionKey,
  PermissionLevel,
  PermissionCheck,
  SystemPermission,
} from '../system/permissions';
import { AccountMetadataFields } from '../system/account';
import { useBrand } from './BrandingContext';
import { useTenant } from './TenantContext';
import { useEditAs } from './hooks/useEditAs';
import { clearPersistedIam } from './util/createParams';
import SaasLogout from '../components/common/saas/SaasLogout';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  session: Session | null;
  // New permission checks using PermissionKey
  hasPermissionKey: (key: PermissionKey) => boolean;
  allowed: (check: PermissionCheck) => boolean;
  getPermissionLevel: (key: PermissionKey) => PermissionLevel | null;
  getGrantorKeys: (key: PermissionKey, level: PermissionLevel) => string[];
  // Convenience methods
  isViewOnly: () => boolean;
  canCreateListing: () => boolean;
  isListingAccessPending: () => boolean;
  hasAnyListingEditPermission: () => boolean;
  hasAnyWritePermission: () => boolean;
  // Rule engine support (uses permissionMap with numeric keys)
  hasPermission: (ruleKey: string | number) => boolean;
  // Edit-as (acting on behalf of another agent)
  editAs: string | null;
  setEditAs: (agentId: string | null) => void;
  isEditAsLoading: boolean;
  editAsError: Error | null;
  checkAuthentication: () => Promise<{
    session?: Session;
    willNotLogin: boolean;
  }>;
  logout: () => Promise<void>;
}

interface ResoHeaders {
  reso_client_header_api_key: string;
  reso_client_header_api_secret: string;
}

export interface Session {
  user: {
    nameID: string;
    email: string;
    firstName: string;
    lastName: string;
    memberMlsId: string;
    agent: SearchAgentResult;
    tenant: string;
  };
  id: string;
  reso: ResoHeaders;
  // New unified permission structure from backend
  permissions: SystemPermission[];
  permissionMap: Record<string, true>;
  permissionKeyMap: Record<string, true>;
  lastAccessed: number;
  createdAt: number;
  metadata?: AccountMetadataFields;
  systemType?: 'RESO' | 'OCUSELL' | string;
}
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);
interface ConfigProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: ConfigProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const { clearBranding } = useBrand();
  const { api, tenant, getConfigValue, isAddEditHost, isTenantFoundInPath } =
    useTenant();

  // Determine if tenant is using permissions from addedit
  const isUsingPermissions = getConfigValue<boolean>('ENABLE_PERMISSIONS');

  // Edit-as: fetch overridden permissions when acting on behalf of another agent
  const {
    editAs,
    setEditAs,
    isEditAsLoading,
    editAsError,
    effectivePermissions,
  } = useEditAs({ api, tenant, isUsingPermissions, session });

  // Compute the frontend URL dynamically
  const frontendUrl = useMemo(() => {
    const { protocol, hostname, port } = window.location;
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }, []);

  // Check session status periodically
  useEffect(() => {
    const checkSessionStatus = async () => {
      if (isLoggedOut) {
        // Nothing to check here, application is stalled
        return;
      }

      try {
        const response = await fetch(`${api}/me`, {
          credentials: 'include', // Important for sending cookies
        });
        if (!response.ok) {
          await logout();
        }
      } catch {
        await logout();
      }
    };

    const interval = setInterval(checkSessionStatus, 60000 * 5); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const logout = async () => {
    try {
      await fetch(`${api}/auth/logout`, {
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during logout API call:', error);
    } finally {
      // Clear branding and edit-as state to ensure they don't persist after logout
      clearBranding();
      clearPersistedIam();

      // Clear local state regardless of logout API success
      setSession(null);
      setIsAuthenticated(false);

      // Fetch the tenant config for logout
      // This is usually a URL, but some tenants will not require a redirect
      // > In those cases, they will generally provide 'LOGOUT_LOCAL..'
      // Handle these cases by only observing HTTP logout configuration
      const logout = getConfigValue<string>('LOGOUT_URL');
      // Handle logout differently based on tenant
      if (!isAddEditHost() && logout && logout.startsWith('http')) {
        window.location.href = logout;
      } else {
        // For other tenants, show the logged out page
        setIsLoggedOut(true);
      }
    }
  };

  // Check by PermissionKey - O(1) lookup using effectivePermissions
  const hasPermissionKey = (key: PermissionKey): boolean => {
    if (!isUsingPermissions) {
      return true;
    }
    return !!effectivePermissions.permissionKeyMap?.[key];
  };

  // Check by rule key (numeric string) - O(1) lookup using effectivePermissions
  // Used by rule engine
  const hasPermission = (ruleKey: string | number): boolean => {
    if (!isUsingPermissions) {
      return true;
    }

    // If permissionMap is empty but permissionKeyMap has entries, user is fully permissed
    if (
      Object.keys(effectivePermissions.permissionMap || {}).length === 0 &&
      Object.keys(effectivePermissions.permissionKeyMap || {}).length > 0
    ) {
      return true;
    }

    return !!effectivePermissions.permissionMap?.[String(ruleKey)];
  };

  // Check permission with optional level
  const allowed = ({ key, level }: PermissionCheck): boolean => {
    if (!isUsingPermissions) {
      return true;
    }

    if (!effectivePermissions.permissionKeyMap?.[key]) {
      return false;
    }

    if (!level) {
      return true;
    }

    return effectivePermissions.permissions.some(
      (p) => p.key === key && p.level === level,
    );
  };

  // Get the highest access level for a permission
  const getPermissionLevel = (key: PermissionKey): PermissionLevel | null => {
    const matching =
      effectivePermissions.permissions?.filter((p) => p.key === key) || [];
    if (!matching.length) return null;

    // Priority: ALL > BROKERAGE > OFFICE > INDIVIDUAL
    if (matching.some((p) => p.level === PermissionLevel.ALL))
      return PermissionLevel.ALL;
    if (matching.some((p) => p.level === PermissionLevel.BROKERAGE))
      return PermissionLevel.BROKERAGE;
    if (matching.some((p) => p.level === PermissionLevel.OFFICE))
      return PermissionLevel.OFFICE;
    return PermissionLevel.INDIVIDUAL;
  };

  // Get grantor keys for a permission at a specific level
  const getGrantorKeys = (
    key: PermissionKey,
    level: PermissionLevel,
  ): string[] => {
    return (effectivePermissions.permissions || [])
      .filter((p) => p.key === key && p.level === level && p.grantorKey)
      .map((p) => p.grantorKey!);
  };

  // Check if user can only view listings (not edit)
  const isViewOnly = (): boolean => {
    if (!isUsingPermissions) return false;
    if (!hasPermissionKey(PermissionKey.VIEW_LISTING)) return false;
    return !hasAnyListingEditPermission();
  };

  // Check if user can create new listings.
  // Backed by the CREATE_LISTING synthetic: RESO emits it additively whenever
  // ADD_OR_EDIT_UNPUBLISHED_LISTINGS is present (Bright behavior unchanged),
  // and Ocusell emits it only to CAN_ENTER_DATA member classes.
  const canCreateListing = (): boolean => {
    return hasPermissionKey(PermissionKey.CREATE_LISTING);
  };

  // Check whether the user's role is flagged as "pending full rollout".
  // Used to show the PA ("Personal Assistant") friendly banner. Never emitted
  // by RESO, so this is always false on Bright.
  const isListingAccessPending = (): boolean => {
    return hasPermissionKey(PermissionKey.LISTING_ACCESS_PENDING);
  };

  // Check if user has any listing edit permission
  const hasAnyListingEditPermission = (): boolean => {
    const editPermissions = [
      PermissionKey.ACCESS_LISTING_MANAGEMENT,
      PermissionKey.ADD_OR_EDIT_UNPUBLISHED_LISTINGS,
      PermissionKey.ADD_EDIT_ALL_OTHER_LISTING_DATA,
      PermissionKey.CAN_EDIT_LISTINGS,
      PermissionKey.PUBLISH_CHANGES_TO_EXISTING_LISTINGS,
      PermissionKey.PUBLISH_NEW_LISTINGS,
      PermissionKey.EDIT_CURRENT_PRICE,
      PermissionKey.EDIT_LISTING_STATUS,
      PermissionKey.CANCEL_LISTINGS,
    ];
    return editPermissions.some((key) => hasPermissionKey(key));
  };

  // Broader than hasAnyListingEditPermission: also counts media-only and
  // map-only permissions. Used to decide whether autosave should run at all.
  // A user with media perms but no data-entry perms (e.g. Ocusell Agent No
  // Input) is NOT view-only for autosave purposes — they can still save
  // photos, documents, open houses, virtual tours, and map pin edits.
  const hasAnyWritePermission = (): boolean => {
    if (hasAnyListingEditPermission()) return true;
    const writePermissions = [
      PermissionKey.UPLOAD_LISTING_PHOTOS,
      PermissionKey.UPLOAD_DOCUMENTS,
      PermissionKey.ADD_EDIT_VIRTUAL_TOURS,
      PermissionKey.ADD_OR_EDIT_BROKER_OPEN_HOUSE,
      PermissionKey.ADD_OR_EDIT_PUBLIC_OPEN_HOUSE,
      PermissionKey.EDIT_LISTING_MAP,
    ];
    return writePermissions.some((key) => hasPermissionKey(key));
  };

  const checkAuthentication = async (): Promise<{
    session?: Session;
    willNotLogin: boolean;
  }> => {
    const login = () => {
      if (!window.location.pathname.includes('auth-success')) {
        const currentUrl = window.location.href;

        // On the SAAS domain we can't assume the tenant is BRIGHT
        //   Tenant is provided in the domain
        //   addedit.mynextgensuite.com/mlsnow -> mlsnow'
        // So in this case, addedit.mynextgensuite.com/ can't be assumed
        // Reject the user with a 404
        if (isAddEditHost() && !isTenantFoundInPath) {
          return {
            willNotLogin: true,
            session: undefined,
          };
        }

        window.location.href = `${api}/auth/login?frontendUrl=${encodeURIComponent(frontendUrl)}&next=${encodeURIComponent(currentUrl)}&tenant=${tenant}`;
      }
      return {
        willNotLogin: false,
        session: undefined,
      };
    };
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${api}/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // Handle unauthenticated state
        return login();
      } else {
        const sessionData = await response.json();
        setSession(sessionData);
        setIsAuthenticated(true);

        return {
          session: sessionData,
          willNotLogin: false,
        };
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error
          : new Error('Authentication check failed'),
      );
      return login();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoggedOut) {
    return <SaasLogout />;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        session,
        // New permission checks
        hasPermissionKey,
        allowed,
        getPermissionLevel,
        getGrantorKeys,
        // Convenience methods
        isViewOnly,
        canCreateListing,
        isListingAccessPending,
        hasAnyListingEditPermission,
        hasAnyWritePermission,
        // Rule engine support
        hasPermission,
        // Edit-as
        editAs,
        setEditAs,
        isEditAsLoading,
        editAsError,
        checkAuthentication,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
